import React, { useEffect, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import {
  Loader2, X, Globe, MapPin, Phone, Mail, RefreshCw, ExternalLink, Check,
  AlertCircle, Sparkles, Send, ThumbsDown, ThumbsUp, Copy, Pencil, Save,
  Star, Compass,
} from "lucide-react";
import { toast } from "sonner";

function scoreColor(score) {
  if (score == null) return "bg-white/10 text-white/60";
  if (score >= 70) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (score >= 40) return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  return "bg-[#E52431]/15 text-[#FFB4BB] border-[#E52431]/30";
}

function AxisCard({ title, axisData }) {
  if (!axisData) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.12em] text-white/55 font-semibold">{title}</div>
        <div className={`px-2 py-0.5 rounded-md text-sm font-semibold border ${scoreColor(axisData.axis_score)}`}>
          {axisData.axis_score}/100
        </div>
      </div>
      {axisData.notes && <p className="text-xs text-white/55 italic mb-3">{axisData.notes}</p>}
      <ul className="space-y-1.5">
        {axisData.breakdown?.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            {b.ok ? (
              <Check size={13} className="text-emerald-400 mt-0.5 shrink-0" />
            ) : (
              <AlertCircle size={13} className="text-[#E52431] mt-0.5 shrink-0" />
            )}
            <span className="flex-1 text-white/70">{b.label}</span>
            <span className="text-white/50 font-mono text-[10px]">
              {b.points}/{b.max}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    raw: { label: "Brut", className: "bg-white/10 text-white/70" },
    pending_validation: { label: "À valider", className: "bg-amber-500/15 text-amber-300 border border-amber-500/30" },
    ready_to_send: { label: "Prêt à envoyer", className: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" },
    rejected: { label: "Rejeté", className: "bg-white/5 text-white/40 border border-white/10" },
  };
  const s = map[status] || { label: status, className: "bg-white/10 text-white/60" };
  return (
    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full font-semibold ${s.className}`}>
      {s.label}
    </span>
  );
}

export default function ProspectDetailPanel({ prospectId, onClose, onRescored }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ audit_text: "", email_subject: "", email_draft: "" });
  const [savingDraft, setSavingDraft] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const refresh = async () => {
    const fresh = await adminApi.get(`/admin/prospects/${prospectId}`);
    setData(fresh.data);
    setDraft({
      audit_text: fresh.data.audit_text || "",
      email_subject: fresh.data.email_subject || "",
      email_draft: fresh.data.email_draft || "",
    });
  };

  useEffect(() => {
    if (!prospectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setEditing(false);
      try {
        const { data } = await adminApi.get(`/admin/prospects/${prospectId}`);
        if (cancelled) return;
        setData(data);
        setDraft({
          audit_text: data.audit_text || "",
          email_subject: data.email_subject || "",
          email_draft: data.email_draft || "",
        });
      } catch (e) {
        if (!cancelled) toast.error("Impossible de charger ce prospect.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  const onRescore = async () => {
    if (!prospectId) return;
    setRescoring(true);
    try {
      const { data } = await adminApi.post(`/admin/prospects/${prospectId}/score`);
      await refresh();
      toast.success(`Score : ${data.score}/100`);
      onRescored?.();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Scoring échoué");
    } finally {
      setRescoring(false);
    }
  };

  const onGenerate = async () => {
    if (!prospectId) return;
    setGenerating(true);
    try {
      await adminApi.post(`/admin/prospects/${prospectId}/generate`);
      await refresh();
      toast.success("Audit + email générés.");
      onRescored?.();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Génération LLM échouée.");
    } finally {
      setGenerating(false);
    }
  };

  const onEnrich = async () => {
    if (!prospectId) return;
    setEnriching(true);
    try {
      const { data: result } = await adminApi.post(`/admin/prospects/${prospectId}/enrich`);
      await refresh();
      if (result.enriched) {
        toast.success(`Enrichi via Google Places — nouveau score : ${result.new_score ?? "—"}/100`);
      } else if (result.no_match) {
        toast.info("Aucun résultat Google Places pour ce prospect.");
      } else {
        toast.warning("Enrichissement non concluant.");
      }
      onRescored?.();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Enrichissement échoué.");
    } finally {
      setEnriching(false);
    }
  };

  const onSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await adminApi.patch(`/admin/prospects/${prospectId}/draft`, draft);
      await refresh();
      setEditing(false);
      toast.success("Modifications enregistrées.");
      onRescored?.();
    } catch (e) {
      toast.error("Sauvegarde échouée.");
    } finally {
      setSavingDraft(false);
    }
  };

  const onChangeStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await adminApi.patch(`/admin/prospects/${prospectId}/status`, { status: newStatus });
      await refresh();
      toast.success(newStatus === "ready_to_send" ? "Prospect approuvé." : "Prospect rejeté.");
      onRescored?.();
    } catch (e) {
      toast.error("Changement de statut échoué.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const onCopyEmail = async () => {
    const subj = data?.email_subject || "";
    const body = data?.email_draft || "";
    const text = `Objet : ${subj}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email copié dans le presse-papiers.");
    } catch (e) {
      toast.error("Copie impossible.");
    }
  };

  if (!prospectId) return null;

  const hasDraft = !!(data?.email_draft || data?.audit_text);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="prospect-detail-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="relative ml-auto w-full max-w-2xl h-full bg-[#0A0A0E] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0A0A0E]/95 backdrop-blur">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[2px] text-white/40 font-semibold">— PROSPECT</div>
            <div className="font-semibold text-white truncate">{data?.nom_entreprise || "…"}</div>
          </div>
          <div className="flex items-center gap-3">
            {data?.status && <StatusBadge status={data.status} />}
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition"
              data-testid="prospect-detail-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
            </div>
          ) : !data ? (
            <p className="text-white/50 text-sm">Prospect introuvable.</p>
          ) : (
            <>
              {/* Top info */}
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {data.site_web && (
                  <a
                    href={data.site_web}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-white/70 hover:text-white transition"
                  >
                    <Globe size={14} className="text-white/40" />
                    <span className="truncate">{data.site_web.replace(/^https?:\/\//, "")}</span>
                    <ExternalLink size={11} />
                  </a>
                )}
                {data.ville && (
                  <div className="flex items-center gap-2 text-white/70">
                    <MapPin size={14} className="text-white/40" /> {data.ville}
                  </div>
                )}
                {data.email_contact && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Mail size={14} className="text-white/40" />
                    <span className="truncate">{data.email_contact}</span>
                  </div>
                )}
                {data.telephone && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Phone size={14} className="text-white/40" /> {data.telephone}
                  </div>
                )}
              </div>

              {/* Total score */}
              <div className="mt-6 flex items-center justify-between p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent">
                <div>
                  <div className="text-[10px] tracking-[2px] text-white/40 font-semibold">SCORE GLOBAL</div>
                  <div className="mt-1 font-outfit text-5xl font-light">
                    {data.score == null ? "—" : (
                      <span className={scoreColor(data.score).split(" ")[1]}>
                        {data.score}<span className="text-xl text-white/50">/100</span>
                      </span>
                    )}
                  </div>
                  {data.score_report?.scored_at && (
                    <div className="text-[10px] text-white/35 mt-1">
                      Scoré le {new Date(data.score_report.scored_at).toLocaleString("fr-FR")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={onEnrich}
                    disabled={enriching}
                    data-testid="prospect-enrich"
                    title="Récupérer site web, téléphone et note Google via Google Places"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-60"
                  >
                    {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass size={13} />}
                    {enriching ? "Enrichissement…" : "Enrichir Google"}
                  </button>
                  <button
                    onClick={onRescore}
                    disabled={rescoring}
                    data-testid="prospect-rescore"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-60"
                  >
                    {rescoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw size={13} />}
                    {rescoring ? "Calcul…" : (data.score == null ? "Scorer" : "Re-scorer")}
                  </button>
                  <button
                    onClick={onGenerate}
                    disabled={generating || data.score == null}
                    data-testid="prospect-generate-llm"
                    title={data.score == null ? "Score le prospect d'abord" : "Générer l'audit + l'email via Claude Sonnet 4.5"}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold gradient-bg text-white hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={13} />}
                    {generating ? "Génération…" : (hasDraft ? "Régénérer" : "Générer audit + email")}
                  </button>
                </div>
              </div>

              {/* Generated content */}
              {hasDraft && (
                <div className="mt-6 space-y-4" data-testid="prospect-generated-content">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs uppercase tracking-[0.12em] text-white/55 font-semibold">Audit & email générés</h3>
                    <div className="flex items-center gap-2">
                      {!editing ? (
                        <button
                          onClick={() => setEditing(true)}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition"
                          data-testid="prospect-edit-draft"
                        >
                          <Pencil size={11} /> Éditer
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => { setEditing(false); setDraft({ audit_text: data.audit_text || "", email_subject: data.email_subject || "", email_draft: data.email_draft || "" }); }}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 transition"
                          >
                            Annuler
                          </button>
                          <button
                            onClick={onSaveDraft}
                            disabled={savingDraft}
                            data-testid="prospect-save-draft"
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition disabled:opacity-60"
                          >
                            {savingDraft ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save size={11} />} Enregistrer
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Audit */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-white/45 font-semibold mb-2">Audit personnalisé</div>
                    {editing ? (
                      <textarea
                        value={draft.audit_text}
                        onChange={(e) => setDraft({ ...draft, audit_text: e.target.value })}
                        rows={10}
                        data-testid="prospect-edit-audit"
                        className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-white/85 leading-relaxed focus:border-[#E52431]/60 outline-none font-light"
                      />
                    ) : (
                      <div className="text-sm text-white/85 leading-relaxed whitespace-pre-line font-light">
                        {data.audit_text}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-white/45 font-semibold">Email de prospection</div>
                      {!editing && (
                        <button
                          onClick={onCopyEmail}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold text-white/55 hover:text-white transition"
                          data-testid="prospect-copy-email"
                        >
                          <Copy size={11} /> Copier
                        </button>
                      )}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.1em] text-white/40 mb-1">Objet</div>
                    {editing ? (
                      <input
                        value={draft.email_subject}
                        onChange={(e) => setDraft({ ...draft, email_subject: e.target.value })}
                        data-testid="prospect-edit-subject"
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-3 focus:border-[#E52431]/60 outline-none"
                      />
                    ) : (
                      <div className="text-sm text-white/95 font-medium mb-3">{data.email_subject || "—"}</div>
                    )}
                    <div className="text-[11px] uppercase tracking-[0.1em] text-white/40 mb-1">Corps</div>
                    {editing ? (
                      <textarea
                        value={draft.email_draft}
                        onChange={(e) => setDraft({ ...draft, email_draft: e.target.value })}
                        rows={10}
                        data-testid="prospect-edit-body"
                        className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-white/85 leading-relaxed focus:border-[#E52431]/60 outline-none font-light"
                      />
                    ) : (
                      <div className="text-sm text-white/85 leading-relaxed whitespace-pre-line font-light">
                        {data.email_draft}
                      </div>
                    )}
                  </div>

                  {/* Validation actions */}
                  {!editing && data.status !== "ready_to_send" && (
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => onChangeStatus("ready_to_send")}
                        disabled={updatingStatus}
                        data-testid="prospect-approve"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs uppercase tracking-[0.12em] font-semibold bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 transition disabled:opacity-60"
                      >
                        <ThumbsUp size={13} /> Approuver
                      </button>
                      <button
                        onClick={() => onChangeStatus("rejected")}
                        disabled={updatingStatus}
                        data-testid="prospect-reject"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/65 hover:bg-white/10 transition disabled:opacity-60"
                      >
                        <ThumbsDown size={13} /> Rejeter
                      </button>
                    </div>
                  )}
                  {!editing && data.status === "ready_to_send" && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm">
                      <Send size={14} />
                      <span>Approuvé. Prêt à être envoyé depuis votre client mail.</span>
                      <button
                        onClick={() => onChangeStatus("pending_validation")}
                        disabled={updatingStatus}
                        className="ml-auto text-xs uppercase tracking-[0.1em] text-emerald-300/80 hover:text-emerald-200"
                      >
                        Réviser
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Google rating display (Iteration 6b — set by Google Places enrichment) */}
              {data.google_rating != null && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200">
                  <Star size={13} className="fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{data.google_rating.toFixed(1)}</span>
                  <span className="text-amber-300/70">/5 · {data.google_rating_count} avis Google</span>
                </div>
              )}

              {/* Axes breakdown */}
              {data.score_report?.axes && (
                <div className="mt-6 space-y-3">
                  <h3 className="text-xs uppercase tracking-[0.12em] text-white/55 font-semibold">Détail du scoring</h3>
                  <AxisCard title="Site web" axisData={data.score_report.axes.site} />
                  <AxisCard title="Réseaux sociaux" axisData={data.score_report.axes.social} />
                  {data.score_report.axes.google && (
                    <AxisCard title="Google Business" axisData={data.score_report.axes.google} />
                  )}
                </div>
              )}
              {data.score_report?.axes?.social?.found && Object.keys(data.score_report.axes.social.found).length > 0 && (
                <div className="mt-4 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 font-semibold mb-2">Réseaux détectés</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.score_report.axes.social.found).map(([net, url]) => (
                      <a
                        key={net}
                        href={`https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/75 hover:text-white hover:bg-white/10 transition inline-flex items-center gap-1"
                      >
                        {net} <ExternalLink size={9} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
